import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { TextField, Box, Autocomplete, FormControlLabel, Switch, Button, FormHelperText, Link, Divider, Grid, Card, Typography, Stack, Slider, Theme } from '@mui/material';
import {  Upload } from '@mui/icons-material';
import { PrimaryButton } from '../buttons/buttons';
import { fileToBase64 } from '../../utils/fileToBase64';
import { downloadBase64File } from '../../utils/downloadBase64File';
import { Body1 } from '../typography/typography';
import { useTranslation } from 'react-i18next';
import TextInput from '../inputs/textInput';
import AsyncSelect from '../inputs/AsyncSelect';
import { toast } from '../../utils/toast';
import DateTimeInput from '../inputs/DateTimeInput';
import { SelectInput } from '../inputs';
import { COUNTRY_NAMES, CountryCode } from '../../models/country';
import { COUNTRY_TO_FLAG } from '../../utils/languageUtils';
import { Mark } from '@mui/material/Slider/useSlider.types';

export type FormSchema = {
  name: string;
  label?: string;
  icon?: React.ReactNode;
  cols?: number;
  labelSx?: object;
  fields: FormSchemaField[];
}

type FieldDefault = string | number | boolean | Date | Array<string | number> | Record<string, unknown> | null | undefined;

export type FormSchemaField = {
  type: 'text' | 'number' | 'email' | 'password' | 'date' | 'datetime' | 'select' | 'async-select' | 'multiline' | 'multiple' | 'switch' | 'upload' | 'divider' | 'country-autocomplete' | 'slider';
  name: string;
  label?: string;
  options?: { key: string | number, value: any }[]
  required?: boolean;
  allowedFileTypes?: string[];
  validationError?: string[];
  rows?: number;
  validationRules?: ((value?: string, meta?: Record<string, any>) => string | undefined)[]
  hide?: boolean;
  disable?: boolean;
  cols?: number;
  sx?: object;
  dividerText?: string;
  dividerIcon?: React.ReactNode;
  sliderMin?: number;
  sliderMax?: number;
  sliderStep?: number;
  sliderMarks?: boolean | Mark[];
  url?: string;
  T?: any;
  props?: any;
  onChange?: (value: any) => void;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => string | number;
  render?: () => React.ReactNode;
  size?: 'small' | 'medium';
  defaultValue?: FieldDefault;
}

export type FormBuilderRef = {
  submit: () => Promise<void>;
  validate: () => boolean;
}

type Props = {
  formData: Record<string, any>;
  formSchema: FormSchema[];
  actionText?: string | React.ReactNode;
  setFormData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  handleSubmit: () => Promise<void>
  onSchemaChange: (schema: FormSchema[]) => void;
  onValidationChange?: (hasErrors: boolean) => void;
  disabled?: boolean;
}

const FormBuilder = forwardRef<FormBuilderRef, Props>(({
  formData,
  formSchema,
  actionText = 'Submit',
  setFormData,
  handleSubmit,
  onSchemaChange,
  onValidationChange,
  disabled = false,
}, ref) => {
  const { t } = useTranslation();

  const [validationError, setValidationError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = async (field: FormSchemaField, value: any, meta?: Record<string, any>) => {
    if (field.onChange) {
      field.onChange(value);
    }
    setFormData(prev => ({
      ...prev,
      [field.name]: value,
      ...(meta?.fileName !== undefined ? { [`${field.name}FileName`]: meta.fileName } : {}),
      ...(meta?.fileType !== undefined ? { [`${field.name}FileType`]: meta.fileType } : {}),
    }));
    handleValidation(field, value, meta);
  };

  const runValidation = (): boolean => {
    const updatedSchema = formSchema.map(section => ({
      ...section,
      fields: section.fields.map(field => validate(field, formData[field.name], formData))
    }));
    onSchemaChange(updatedSchema);
    const hasErrors = updatedSchema.some(section => section.fields.some(item => item.validationError?.length));
    setValidationError(hasErrors);
    onValidationChange?.(hasErrors);
    return !hasErrors;
  };

  const handleAction = async () => {
    try {
      if (!runValidation()) return;
      setLoading(true);
      await handleSubmit();
    } catch {
      toast.error(t('An error occurred while submitting the form'));
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    submit: handleAction,
    validate: runValidation,
  }));

  const handleValidation = (field: FormSchemaField, value: any, meta?: object) => {
    const updatedSchema = formSchema.map(section => ({
      ...section,
      fields: section.fields.map(item => {
        if (item.name === field.name) {
          return validate(item, value, meta);
        }
        return item;
      })
    }));
    onSchemaChange(updatedSchema);
    const hasErrors = updatedSchema.some(section => section.fields.some(item => item.validationError?.length));
    setValidationError(hasErrors);
    onValidationChange?.(hasErrors);
  };

  const validate = (item: FormSchemaField, value: string | string[] | number, meta?: object): FormSchemaField => {
    if (!item.validationRules) item.validationRules = [];
    item.validationError = [];
    if (item.required && (Array.isArray(value) ? !value.length : (value !== 0 && !value))) {
      item.validationError.push('');
    }
    item.validationRules.forEach((rule) => {
      const values = Array.isArray(value) ? value : (value === undefined || value === null || value === '' ? [] : [`${value}`]);
      values.forEach((v: string) => {
        const validationText = rule(v, meta);
        if (validationText) {
          item.validationError?.push(validationText);
        }
      });
    });
    return item;
  };

  const actions = actionText && (typeof actionText === 'string' ? actionText.trim() !== '' : true)
    ? <Box textAlign="right"> {typeof actionText === 'string'
      ? <PrimaryButton onClick={handleAction} loading={loading} disabled={validationError}>{actionText}</PrimaryButton>
      : actionText} </Box>
    : null;

  return (
    <Box component="form">
      <Grid container alignItems="center">
        {formSchema.map(section => <FormBuilderCard key={section.name}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <span>{section.icon}</span>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: (theme: any) => theme.typography.fontSizes.body, ...section.labelSx }}>
              {section.label}
            </Typography>
          </Stack>
          <Grid container spacing={1} alignItems="center">
            {section.fields.map((rawField) => {
              const field = disabled ? { ...rawField, disable: true } : rawField;
              switch (field.type) {
                case 'multiline':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <TextInput
                          key={field.name}
                          name={field.name}
                          multiline
                          label={field.label}
                          required={field.required || false}
                          defaultValue={formData[field.name] || ''}
                          // Used onBlur instead of onChange, because onChange causes re-renders on every keystroke, which can lead to laggy input.
                          onBlur={e => handleChange(field, e.target.value)}
                          fullWidth
                          size={field.size || 'small'}
                          rows={field.rows}
                          sx={{
                            backgroundColor: 'background.white',
                            ...field.sx
                          }}
                          disabled={field.disable}
                          error={field.validationError?.length ? true : false}
                          helperText={field.validationError?.join(', ')}
                        >
                          {field.render && field.render()}
                        </TextInput>
                      </Grid>
                  );

                case 'date':
                case 'datetime':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <DateTimeInput
                          key={field.name}
                          name={field.name}
                          type={field.type}
                          label={field.label}
                          required={field.required || false}
                          InputLabelProps={{ shrink: true }}
                          value={formData[field.name] ? formData[field.name] : null}
                          onChange={(value) => handleChange(field, value, formData)}
                          fullWidth
                          size={field.size || 'small'}
                          sx={{ backgroundColor: 'background.white', ...field.sx }}
                          disabled={field.disable}
                          error={field.validationError?.length ? true : false}
                          helperText={field.validationError?.join(', ')}
                        />
                      </Grid>
                  );

                case 'select':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <SelectInput
                          key={field.name}
                          name={field.name}
                          label={field.label}
                          required={field.required || false}
                          value={formData[field.name]}
                          onChange={e => handleChange(field, e.target.value)}
                          fullWidth
                          size={field.size || 'small'}
                          sx={{ backgroundColor: 'background.white', ...field.sx }}
                          disabled={field.disable}
                          error={field.validationError?.length ? true : false}
                          options={field.options || []}
                        />
                      </Grid>
                  );

                case 'country-autocomplete':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <Box>
                          <Autocomplete
                            options={Object.values(CountryCode).map((code) => ({
                              label: COUNTRY_NAMES[code],
                              value: code,
                              flag: COUNTRY_TO_FLAG[code] || ''
                            }))}
                            getOptionLabel={(option) => option.label}
                            value={formData[field.name] ? Object.values(CountryCode).map((code) => ({
                              label: COUNTRY_NAMES[code],
                              value: code,
                              flag: COUNTRY_TO_FLAG[code] || ''
                            })).find(opt => opt.value === formData[field.name]) || null : null}
                            onChange={(e, newValue) => handleChange(field, newValue?.value || '')}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <span>{option.flag}</span>
                                <span>{option.label}</span>
                              </Box>
                            )}
                            slotProps={{
                              popper: {
                                placement: 'bottom-start',
                                modifiers: [
                                  {
                                    name: 'flip',
                                    enabled: false,
                                  },
                                  {
                                    name: 'preventOverflow',
                                    enabled: true,
                                  },
                                ],
                              },
                            }}
                            renderInput={(params) => {
                              const selectedOption = formData[field.name] ? Object.values(CountryCode).map((code) => ({
                                label: COUNTRY_NAMES[code],
                                value: code,
                                flag: COUNTRY_TO_FLAG[code] || ''
                              })).find(opt => opt.value === formData[field.name]) : null;
                              return (
                                <TextInput
                                  {...params}
                                  label={field.label}
                                  fullWidth
                                  size={field.size || 'small'}
                                  required={field.required || false}
                                  error={field.validationError?.length ? true : false}
                                  helperText={field.validationError?.join(', ')}
                                  sx={{
                                    backgroundColor: 'background.white',
                                    ...field.sx
                                  }}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: selectedOption?.flag ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                        <span>{selectedOption.flag}</span>
                                      </Box>
                                    ) : null,
                                  }}
                                />
                              );
                            }}
                            disabled={field.disable}
                          />
                        </Box>
                      </Grid>
                  );

                case 'async-select':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <AsyncSelect<any>
                          key={field.name}
                          url={field.url as string}
                          label={field.label || ''}
                          getOptionLabel={field.getOptionLabel as (option: any) => string}
                          getOptionValue={field.getOptionValue as (option: any) => string | number}
                          handleChange={value => handleChange(field, value)}
                          value={formData[field.name]}
                          size={field.size || 'small'}
                          disabled={field.disable}
                          error={field.validationError?.length ? true : false}
                          helperText={field.validationError?.join(', ')}
                        />
                      </Grid>
                  );

                case 'multiple':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <Autocomplete
                          key={field.name}
                          multiple
                          freeSolo
                          options={field.options ? field.options.map(option => option.key) : []}
                          getOptionLabel={option => option.replaceAll('_', ' ').toLowerCase()}
                          autoSelect={true}
                          value={formData[field.name] ?? []}
                          onChange={(e, values) => handleChange(field, values)}
                          disabled={field.disable}
                          openOnFocus
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                            ...field.sx
                          }}
                          renderInput={(params) => (
                            <TextInput
                              {...params}
                              label={field.label}
                              required={field.required}
                              size={field.size || 'small'}
                              error={field.validationError?.length ? true : false}
                              helperText={field.validationError?.join(', ')}
                            />
                          )}
                        />
                      </Grid>
                  );

                case 'switch':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <Box>
                          <FormControlLabel
                            control={
                              <Switch
                                key={field.name}
                                name={field.name}
                                checked={formData[field.name] ?? true}
                                disabled={field.disable}
                                onChange={(e, checked) => handleChange(field, checked)}
                                sx={field.sx}
                                color="primary"
                              />}
                            label={field.render ? field.render() : field.label}
                          />
                        </Box>
                      </Grid>
                  );

                case 'upload': {
                  const uploadedContent = formData[`${field.name}`];
                  const uploadedHandle = formData[`${field.name}Handle`];
                  const uploadedFileName = formData[`${field.name}FileName`];
                  const isPdf = (value: any) => typeof value === 'string' && value.startsWith('data:application/pdf');
                  const isImage = (value: any) => typeof value === 'string' && value.startsWith('data:image');
                  return (
                    field.hide ? null :
                      <React.Fragment key={field.name}>
                        <Grid size={field.cols || 12} marginTop={1}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Button
                              key={field.name}
                              component="label"
                              role={undefined}
                              variant="contained"
                              tabIndex={-1}
                              sx={{
                                backgroundColor: 'background.white',
                                color: field.validationError?.length ? 'error.main' : 'text.dark',
                                border: field.validationError?.length ? '1px solid' : 'none',
                                borderColor: field.validationError?.length ? 'error.main' : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'background.grey',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: field.validationError?.length ? 'error.main' : 'text.dark',
                                },
                                ...field.sx
                              }}
                              startIcon={<Upload />}
                            >
                              {field.label}
                              <TextField
                                type="file"
                                sx={{ display: 'none' }}
                                onChange={async (e: any) => {
                                  const file = e.target.files[0];
                                  const content = await fileToBase64(file);
                                  handleChange(field, content, { fileType: file.type, fileName: file.name });
                                }}
                              />
                            </Button>
                            {(field.validationError?.length ?
                              <FormHelperText sx={{ display: 'inline-block' }} error>
                                {field.validationError?.join(', ')}
                              </FormHelperText> : ''
                            )}
                          </Box>

                          {isPdf(uploadedHandle) &&
                            <Box mt={0.5}>
                              <Link underline='hover' href="#" onClick={() => downloadBase64File(uploadedHandle, uploadedFileName || 'attachment.pdf')}>{uploadedFileName || t('attachment')}</Link>
                            </Box>}

                          {isPdf(uploadedContent) &&
                            <Box mt={0.5}>
                              <Link underline='hover' href="#" onClick={() => downloadBase64File(uploadedContent, uploadedFileName || 'attachment.pdf')}>{uploadedFileName || t('attachment')}</Link>
                            </Box>}

                          {isImage(uploadedContent) &&
                            <Box mt={0.5} display="inline-block">
                              <img
                                src={uploadedContent}
                                alt="Uploaded Preview"
                                style={{ maxWidth: '60px', maxHeight: '60px', borderRadius: '8px', border: '1px solid #ccc', padding: '2px' }}
                              />
                            </Box>}
                        </Grid>
                      </React.Fragment>
                  );
                }

                case 'divider':
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1} marginBottom={-2}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {field.dividerIcon}
                          <Body1 fontWeight={600} fontSize={(theme: any) => theme.typography.fontSizes.body}>{field.dividerText}</Body1>
                        </Box>
                        <Divider sx={{ marginTop: 1, marginBottom: 1 }} />
                      </Grid>
                  );

                case 'slider':
                  return (
                    field.hide ? null :
                      (() => {
                        // Only two marks: [breakEnd, driveEnd]
                        const value = [...(formData[field.name] ? formData[field.name] : (field.defaultValue || []))];
                        const min = field.sliderMin ?? 0;
                        let max = field.sliderMax ?? 0;
                        if (value !== undefined && value.length > 2) { // Pop max if present
                          max = value.pop();
                        }
                        // Ensure value is [breakEnd, driveEnd]
                        const v0 = value[0] ?? min;
                        const v1 = value[1] ?? min;
                        // Clamp and sort values
                        const sorted = [v0, v1].map(v => Math.max(min, Math.min(max, v))).sort((a, b) => a - b);
                        const start = ((sorted[0] - min) / (max - min)) * 100;
                        const mid = ((sorted[1] - min) / (max - min)) * 100;
                        // Theme-aware colors
                        const getColors = (theme: Theme) => {
                          const isDark = theme.palette.mode === 'dark';
                          return {
                            break: isDark ? 'LightGray' : 'DarkGray', // gray
                            drive: isDark ? 'LimeGreen' : 'ForestGreen', // green
                            admin: isDark ? 'Gold' : 'DarkOrange', // amber
                          };
                        };
                        const gradient = (theme: Theme) => {
                          const c = getColors(theme);
                          return `linear-gradient(90deg, ${c.break} 0%, ${c.break} ${start}%, ${c.drive} ${start}%, ${c.drive} ${mid}%, ${c.admin} ${mid}%, ${c.admin} 100%)`;
                        };
                        return (
                          <>
                            <label>{field.label}</label>
                            <Slider
                              value={sorted}
                              min={min}
                              max={max}
                              step={field.sliderStep ?? 1}
                              marks={field.sliderMarks ?? false}
                              onChange={(_, value) => handleChange(field, [...value, max])}
                              valueLabelDisplay="auto"
                              disableSwap
                              getAriaLabel={() => field.label || 'Slider'}
                              sx={(theme) => ({
                                height: 8,
                                '& .MuiSlider-rail': {
                                  background: gradient(theme),
                                  opacity: 1,
                                },
                                '& .MuiSlider-track': {
                                  background: 'transparent',
                                },
                                // Thumb coloring
                                '& .MuiSlider-thumb': {
                                  width: 20,
                                  height: 20,
                                  boxShadow: 2,
                                },
                                '& .MuiSlider-thumb[data-index="0"]': {
                                  backgroundColor: getColors(theme).drive,
                                },
                                '& .MuiSlider-thumb[data-index="1"]': {
                                  backgroundColor: getColors(theme).admin,
                                },
                              })}
                            />
                            <span>
                              <span style={{ color: 'inherit', background: 'none', fontWeight: 500 }}>
                                <Box component="span" sx={{ color: (theme) => getColors(theme).break }}>Break: {sorted[0] - min} min</Box>{' | '}
                                <Box component="span" sx={{ color: (theme) => getColors(theme).drive }}>Drive: {sorted[1] - sorted[0]} min</Box>{' | '}
                                <Box component="span" sx={{ color: (theme) => getColors(theme).admin }}>Admin: {max - sorted[1]} min</Box>
                              </span>
                            </span>
                          </>
                        );
                      })()
                  );

                default:
                  return (
                    field.hide ? null :
                      <Grid key={field.name} size={field.cols || 12} marginTop={1}>
                        <TextInput
                          key={field.name}
                          type={field.type}
                          name={field.name}
                          label={field.label}
                          required={field.required || false}
                          value={formData[field.name] || ''}
                          onChange={e => handleChange(field, e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                          fullWidth
                          size={field.size || 'small'}
                          sx={{
                            backgroundColor: 'background.white',
                            ...field.sx
                          }}
                          disabled={field.disable}
                          error={field.validationError?.length ? true : false}
                          helperText={field.validationError?.join(', ')}
                        />
                      </Grid>
                  );
              }
            })}
          </Grid>
        </FormBuilderCard>
        )}
        {actions && <Grid size={12}>{actions}</Grid>}
        {validationError && (
          <FormHelperText error sx={{ textAlign: 'right' }}>{t('Some fields are missing or need to be updated.')}</FormHelperText>
        )}
      </Grid>
    </Box>
  );
});

FormBuilder.displayName = 'FormBuilder';

export default FormBuilder;

const FormBuilderCard = (props: any) => <Card sx={{ padding: 2, mb: { xs: 2, sm: 2, md: 2 }, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', width: '100%' }} {...props} />;
